<template>
    <div class="time-slider-container">
        <div class="slider-track" ref="trackRef" @mousedown="onTrackMouseDown">
            <div class="slider-fill" :style="fillStyle"></div>

            <!-- Range Drag Handle (The bar itself) -->
            <div class="slider-range-drag" :style="rangeDragStyle" @mousedown.stop="onRangeMouseDown"></div>

            <!-- Start Handle -->
            <div class="slider-handle start" :style="startHandleStyle" @mousedown.stop="onStartMouseDown">
                <span class="tooltip">{{ formatTime(modelValue[0]) }}</span>
            </div>

            <!-- End Handle -->
            <div class="slider-handle end" :style="endHandleStyle" @mousedown.stop="onEndMouseDown">
                <span class="tooltip">{{ formatTime(modelValue[1]) }}</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, toRefs, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
    min: { type: Number, required: true },
    max: { type: Number, required: true },
    modelValue: { type: Array, required: true } // [start, end]
});

const emit = defineEmits(['update:modelValue']);

const trackRef = ref(null);
const dragging = ref(null); // 'start', 'end', 'range'
const dragStartX = ref(0);
const initialVal = ref(0);
const initialRange = ref([0, 0]);

const range = computed(() => props.max - props.min);

function getPercent(value) {
    if (range.value === 0) return 0;
    return ((value - props.min) / range.value) * 100;
}

const startPercent = computed(() => getPercent(props.modelValue[0]));
const endPercent = computed(() => getPercent(props.modelValue[1]));

const fillStyle = computed(() => ({
    left: `${startPercent.value}%`,
    width: `${endPercent.value - startPercent.value}%`
}));

const rangeDragStyle = computed(() => ({
    left: `${startPercent.value}%`,
    width: `${endPercent.value - startPercent.value}%`,
    cursor: dragging.value === 'range' ? 'grabbing' : 'grab'
}));

const startHandleStyle = computed(() => ({
    left: `${startPercent.value}%`
}));

const endHandleStyle = computed(() => ({
    left: `${endPercent.value}%`
}));

function formatTime(ts) {
    if (!ts) return '';
    const date = new Date(ts * 1000);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function getValueFromX(x) {
    const rect = trackRef.value.getBoundingClientRect();
    const percent = Math.min(1, Math.max(0, (x - rect.left) / rect.width));
    return props.min + (percent * range.value);
}

function onStartMouseDown(e) {
    dragging.value = 'start';
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
}

function onEndMouseDown(e) {
    dragging.value = 'end';
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
}

function onRangeMouseDown(e) {
    dragging.value = 'range';
    dragStartX.value = e.clientX;
    initialRange.value = [...props.modelValue];
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
}

function onTrackMouseDown(e) {
    // Jump to position logic could go here, but usually safer to just ignore click-jump for dual handle
}

function onMouseMove(e) {
    if (!dragging.value) return;

    let newVal = getValueFromX(e.clientX);
    let newStart = props.modelValue[0];
    let newEnd = props.modelValue[1];

    if (dragging.value === 'start') {
        newStart = Math.min(newVal, newEnd);
        // Clamp
        newStart = Math.max(props.min, newStart);
    } else if (dragging.value === 'end') {
        newEnd = Math.max(newVal, newStart);
        // Clamp
        newEnd = Math.min(props.max, newEnd);
    } else if (dragging.value === 'range') {
        const deltaPx = e.clientX - dragStartX.value;
        const rect = trackRef.value.getBoundingClientRect();
        const deltaVal = (deltaPx / rect.width) * range.value;

        const duration = initialRange.value[1] - initialRange.value[0];

        newStart = initialRange.value[0] + deltaVal;
        newEnd = initialRange.value[1] + deltaVal;

        // Clamp range
        if (newStart < props.min) {
            newStart = props.min;
            newEnd = newStart + duration;
        }
        if (newEnd > props.max) {
            newEnd = props.max;
            newStart = newEnd - duration;
        }
    }

    // console.log('TimeWindowSlider emitting:', [newStart, newEnd]);
    emit('update:modelValue', [newStart, newEnd]);
}

function onMouseUp() {
    dragging.value = null;
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
}

</script>

<style scoped>
.time-slider-container {
    width: 100%;
    height: 40px;
    display: flex;
    align-items: center;
    padding: 0 10px;
    box-sizing: border-box;
    user-select: none;
}

.slider-track {
    width: 100%;
    height: 6px;
    background-color: #ddd;
    border-radius: 3px;
    position: relative;
}

.slider-fill {
    position: absolute;
    height: 100%;
    background-color: #007bff;
    opacity: 0.3;
    border-radius: 3px;
}

.slider-range-drag {
    position: absolute;
    top: -5px;
    bottom: -5px;
    /* Make click area taller */
    background: transparent;
    z-index: 5;
}

.slider-handle {
    width: 16px;
    height: 16px;
    background-color: #fff;
    border: 2px solid #007bff;
    border-radius: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    cursor: grab;
    z-index: 10;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.slider-handle:active {
    cursor: grabbing;
    background-color: #007bff;
}

.tooltip {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75rem;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0.8;
}
</style>
